import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Avatar,
  Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { authAPI } from "../../services/authAPI";
import { useAuthStore } from "../../stores/authStore";

const profileSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  phone: yup.string().optional(),
});

const passwordSchema = yup.object({
  currentPassword: yup.string().required("Current password is required"),
  newPassword: yup
    .string()
    .min(6, "New password must be at least 6 characters")
    .required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your new password"),
});

type ProfileFormData = yup.InferType<typeof profileSchema>;
type PasswordFormData = yup.InferType<typeof passwordSchema>;

const ProfilePage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<"profile" | "password" | "2fa">(
    "profile"
  );
  const [setupSecret, setSetupSecret] = useState<string | null>(null);
  const [setupOtpauthUrl, setSetupOtpauthUrl] = useState<string | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const isSuperAdmin =
    user?.role === "SUPER_ADMIN" || user?.role === "superadmin";

  const { data: twoFaStatus, refetch: refetch2fa } = useQuery({
    queryKey: ["2fa-status"],
    queryFn: () => authAPI.get2faStatus(),
    enabled: isSuperAdmin,
  });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const {
    control: profileControl,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
    },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormData>({
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const { mutate: updateProfile } = useMutation({
    mutationFn: (data: { name: string; email: string; phone?: string }) =>
      authAPI.updateUser(data),
    onSuccess: (updatedUser) => {
      updateUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: ["user"] });
      setAlert({ type: "success", message: "Profile updated successfully!" });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to update profile",
      });
    },
  });

  const { mutate: changePassword } = useMutation({
    mutationFn: (data: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) => authAPI.changePassword(data),
    onSuccess: () => {
      resetPassword();
      setAlert({ type: "success", message: "Password changed successfully!" });
    },
    onError: (error: any) => {
      const apiMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors
              .map((e: { message?: string }) => e?.message)
              .filter(Boolean)
              .join(", ")
          : null);
      setAlert({
        type: "error",
        message: apiMsg || "Failed to change password",
      });
    },
  });

  const onProfileSubmit = (data: ProfileFormData) => {
    updateProfile(data);
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    });
  };

  const { mutate: start2faSetup, isPending: starting2fa } = useMutation({
    mutationFn: () => authAPI.setup2fa(),
    onSuccess: (data) => {
      setSetupSecret(data.secret);
      setSetupOtpauthUrl(data.otpauthUrl);
      setAlert({
        type: "success",
        message: "Scan the QR code in Google Authenticator, then enter a code to confirm.",
      });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to start 2FA setup",
      });
    },
  });

  const { mutate: confirm2fa, isPending: confirming2fa } = useMutation({
    mutationFn: () => authAPI.confirm2fa(confirmCode),
    onSuccess: () => {
      setSetupSecret(null);
      setSetupOtpauthUrl(null);
      setConfirmCode("");
      void refetch2fa();
      setAlert({ type: "success", message: "Two-factor authentication enabled." });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Invalid authenticator code",
      });
    },
  });

  const { mutate: disable2fa, isPending: disabling2fa } = useMutation({
    mutationFn: () =>
      authAPI.disable2fa({ password: disablePassword, totpCode: disableCode }),
    onSuccess: () => {
      setDisablePassword("");
      setDisableCode("");
      void refetch2fa();
      setAlert({ type: "success", message: "Two-factor authentication disabled." });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to disable 2FA",
      });
    },
  });

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile Settings
      </Typography>

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Avatar
                sx={{ width: 80, height: 80, mx: "auto", mb: 2 }}
                src={`https://ui-avatars.com/api/?name=${user?.name}&background=random`}
                alt={user?.name}
              />
              <Typography variant="h6" gutterBottom>
                {user?.name}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                {user?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role: {user?.role}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Button
                  sx={{ mr: 2 }}
                  color={activeTab === "profile" ? "primary" : "inherit"}
                  onClick={() => setActiveTab("profile")}
                >
                  Profile Information
                </Button>
                <Button
                  color={activeTab === "password" ? "primary" : "inherit"}
                  onClick={() => setActiveTab("password")}
                >
                  Change Password
                </Button>
                {isSuperAdmin && (
                  <Button
                    color={activeTab === "2fa" ? "primary" : "inherit"}
                    onClick={() => setActiveTab("2fa")}
                  >
                    Two-factor auth
                  </Button>
                )}
              </Box>

              {activeTab === "profile" && (
                <form onSubmit={handleProfileSubmit(onProfileSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="name"
                        control={profileControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Full Name"
                            error={!!profileErrors.name}
                            helperText={profileErrors.name?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="email"
                        control={profileControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Email Address"
                            error={!!profileErrors.email}
                            helperText={profileErrors.email?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="phone"
                        control={profileControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Phone Number"
                            error={!!profileErrors.phone}
                            helperText={profileErrors.phone?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" sx={{ mt: 2 }}>
                        Update Profile
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}

              {activeTab === "password" && (
                <form onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Controller
                        name="currentPassword"
                        control={passwordControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Current Password"
                            type="password"
                            error={!!passwordErrors.currentPassword}
                            helperText={passwordErrors.currentPassword?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="newPassword"
                        control={passwordControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="New Password"
                            type="password"
                            error={!!passwordErrors.newPassword}
                            helperText={passwordErrors.newPassword?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="confirmPassword"
                        control={passwordControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Confirm New Password"
                            type="password"
                            error={!!passwordErrors.confirmPassword}
                            helperText={passwordErrors.confirmPassword?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" sx={{ mt: 2 }}>
                        Change Password
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}

              {activeTab === "2fa" && isSuperAdmin && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Super Admin accounts can require a 6-digit authenticator code at
                    login (Google Authenticator, Authy, etc.).
                  </Typography>
                  <Alert severity={twoFaStatus?.enabled ? "success" : "info"} sx={{ mb: 2 }}>
                    Status: {twoFaStatus?.enabled ? "Enabled" : "Not enabled"}
                  </Alert>

                  {!twoFaStatus?.enabled && (
                    <>
                      {!setupSecret ? (
                        <Button
                          variant="contained"
                          disabled={starting2fa}
                          onClick={() => start2faSetup()}
                        >
                          {starting2fa ? "Starting…" : "Set up authenticator"}
                        </Button>
                      ) : (
                        <Grid container spacing={2}>
                          {setupOtpauthUrl && (
                            <Grid item xs={12} sx={{ textAlign: "center" }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Scan with authenticator app
                              </Typography>
                              <Box
                                sx={{
                                  display: "inline-block",
                                  p: 1.5,
                                  bgcolor: "white",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <QRCodeSVG value={setupOtpauthUrl} size={180} />
                              </Box>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">
                              Manual setup key
                            </Typography>
                            <TextField
                              fullWidth
                              value={setupSecret}
                              InputProps={{ readOnly: true }}
                              sx={{ mt: 1 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Confirm with 6-digit code"
                              value={confirmCode}
                              onChange={(e) =>
                                setConfirmCode(
                                  e.target.value.replace(/\D/g, "").slice(0, 6)
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              disabled={confirming2fa || confirmCode.length !== 6}
                              onClick={() => confirm2fa()}
                            >
                              {confirming2fa ? "Confirming…" : "Enable 2FA"}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </>
                  )}

                  {twoFaStatus?.enabled && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Current password"
                          type="password"
                          value={disablePassword}
                          onChange={(e) => setDisablePassword(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Authenticator code"
                          value={disableCode}
                          onChange={(e) =>
                            setDisableCode(
                              e.target.value.replace(/\D/g, "").slice(0, 6)
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          color="warning"
                          disabled={
                            disabling2fa ||
                            !disablePassword ||
                            disableCode.length !== 6
                          }
                          onClick={() => disable2fa()}
                        >
                          {disabling2fa ? "Disabling…" : "Disable 2FA"}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
